"use client";

/**
 * Pause a looping illustration while it is off screen.
 *
 * The landing page carries a dozen decorative SVG loops, and a phone at
 * the top of the page was running 72 of them where nobody could see any
 * (measured 2026-09-21 at 390 px with the CPU throttled 4x: 53 of 1 126
 * frames over 33 ms, against 5 of 1 384 with the loops stopped, so they
 * were about 90% of the jank on that page).
 *
 * `content-visibility: auto` looks like the obvious fix but it is
 * ignored on an <svg> root, which does not take layout containment, so
 * this observes the element instead and toggles a class that sets
 * `animation-play-state: paused` on it and everything inside.
 *
 * The element starts paused and is released only once it is near the
 * viewport, so a page load never pays for art the visitor has not
 * reached. Where IntersectionObserver is missing the element simply
 * animates as before.
 */
import { useEffect, useRef } from "react";

export function useScenePause<T extends Element>() {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      el.classList.remove("scene-paused");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) el.classList.toggle("scene-paused", !e.isIntersecting);
      },
      // A little ahead of the fold, so the motion has already settled
      // into its loop by the time the card is actually read.
      { rootMargin: "150px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}
